import { lazy, Suspense } from 'react'

import { createFileRoute } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'

import { z } from 'zod'

import { RouteErrorComponent } from '@/features/app-shell/route-error-component'

import { ROLES } from '@/shared/auth/constants'
import { useHasActiveSKU } from '@/shared/auth/hooks/use-has-valid-sku'
import { roleAuthGuard } from '@/shared/auth/utils/router'
import { TableFullSkeleton } from '@/shared/data-table/components/table-skeleton'
import { usePanelActions } from '@/shared/panel/store'

import {
	SettingsCard,
	SettingsCardTitle,
} from '@/components/common/settings-card'

import type { TenantUser } from '@/features/tenant/types'

const TeamSettingsTable = lazy(
	() => import('@/features/tenant/components/team-list'),
)

const UserPanel = lazy(
	() => import('@/features/tenant/components/user/user-panel'),
)

const searchSchema = z.object({
	q: z.string().optional(),
	panel: z.string().optional(),
	filters: z.record(z.string(), z.string()).optional(),
})

type Search = z.infer<typeof searchSchema>

export const Route = createFileRoute(
	'/_dashboard/settings/tenant-settings/members',
)({
	validateSearch: zodValidator(searchSchema),

	beforeLoad: () => roleAuthGuard(ROLES.Admin),

	head: () => ({
		meta: [
			{
				title: 'Members | BitLyft AIR®',
			},
		],
	}),

	component: RouteComponent,
	pendingComponent: RouteSkeleton,
	errorComponent: RouteErrorComponent,
})

function RouteComponent() {
	const navigate = Route.useNavigate()
	const isActiveSKU = useHasActiveSKU()

	const { createPanel, close } = usePanelActions()

	const handleSelectUser = (
		user: TenantUser | null,
		mode: 'edit' | 'create' = 'create',
	) => {
		if (!isActiveSKU) return

		const isEditing = mode === 'edit'

		createPanel({
			title: isEditing ? 'Edit User' : 'Create User',
			description: isEditing ? 'Update a user settings' : 'Create a new user',
			render: () => <UserPanel user={user} mode={mode} onSuccess={close} />,
			onClose: handleClose,
		})

		navigate({
			search: (old: Search) => ({
				...old,
				panel: encodeURIComponent(user?.username ?? 'new'),
			}),
		})
	}

	const handleClose = () => {
		navigate({
			search: (old: Search) => ({
				...old,
				panel: undefined,
			}),
		})
	}

	return (
		<SettingsCard>
			<SettingsCardTitle>Members</SettingsCardTitle>

			<Suspense fallback={<TableFullSkeleton className="w-full" rows={10} />}>
				<TeamSettingsTable onSelectUser={handleSelectUser} />
			</Suspense>
		</SettingsCard>
	)
}

function RouteSkeleton() {
	return (
		<SettingsCard>
			<SettingsCardTitle>Members</SettingsCardTitle>

			<TableFullSkeleton className="w-full" rows={10} />
		</SettingsCard>
	)
}
